import React, { useEffect, useState } from "react";
import { radStudyApi, apiError } from "../../api/lab";
import { useApp } from "../../AppProvider";
import { Modal, Button, Field } from "../../Components/UI/ui";
import { LATERALITY, CONTRAST_ROUTES, toLocalDateTimeInput, modalityLabel } from "../../lib/lab";

// "Scan performed" (radiology doc §3.2). Mints the booking's accession, moves
// it to IN_PROCESS and records who ran the scanner. The safety checklist is
// ours to enforce — the server stores the text but validates nothing.
const AcquireModal = ({ open, onClose, study, rooms = [], onDone }) => {
  const { toast } = useApp();
  const [f, setF] = useState({ performedAt: "", room: "", laterality: "", safetyNotes: "", note: "" });
  const [contrast, setContrast] = useState({ used: false, agent: "", dose: "", route: "IV" });
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (p) => setF((x) => ({ ...x, ...p }));

  const modality = study?.modality;
  const isMR = modality === "MRI";
  const ionising = ["Xray", "CT", "Mammography", "DEXA", "OPG", "PET-CT", "NuclearMedicine", "Fluoroscopy", "IVP"].includes(modality);

  useEffect(() => {
    if (!open) return;
    setF({ performedAt: "", room: "", laterality: study?.laterality || "", safetyNotes: "", note: "" });
    setContrast({ used: false, agent: "", dose: "", route: "IV" });
    setChecks({});
    setErr(null);
  }, [open, study]);

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      // Fold the ticked checks into the one free-text line the server stores
      const notes = [
        checks.pregnancy && "Pregnancy screening done",
        checks.implants && "No MR-unsafe implants",
        checks.allergy && "No known contrast allergy",
        f.safetyNotes.trim(),
      ].filter(Boolean).join("; ");
      const body = {
        ...(f.performedAt ? { performedAt: new Date(f.performedAt).toISOString() } : {}),
        ...(f.room ? { room: f.room } : {}),
        ...(f.laterality ? { laterality: f.laterality } : {}),
        ...(notes ? { safetyNotes: notes } : {}),
        ...(f.note.trim() ? { note: f.note.trim() } : {}),
        contrast: contrast.used
          ? { used: true, agent: contrast.agent.trim(), dose: contrast.dose.trim(), route: contrast.route }
          : { used: false },
      };
      const res = await radStudyApi.acquired(study._id, body);
      toast.success(`Scan recorded · accession ${res.accession}`);
      onDone?.(res);
      onClose();
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  if (!study) return null;
  const roomsFor = rooms.filter((r) => !r.modality || r.modality === modality);

  return (
    <Modal open={open} onClose={onClose} title={`Scan performed · ${study.templateSnapshot?.name || study.modality}`}
      footer={<>
        {err && <span className="lab-danger-text" style={{ marginRight: "auto" }}>{err}</span>}
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={busy} onClick={submit}>Mark performed</Button>
      </>}>
      <div className="lab-muted">
        {study.patientName} · {modalityLabel(modality)}{study.bodyPart ? ` · ${study.bodyPart}` : ""}. This assigns the booking's accession number — write it on the film jacket.
      </div>

      <div className="lab-grid cols-3">
        <Field label="Performed at" hint="Leave blank for now">
          <input type="datetime-local" className="lab-input" value={f.performedAt} max={toLocalDateTimeInput()} onChange={(e) => set({ performedAt: e.target.value })} />
        </Field>
        <Field label="Room / machine">
          <select className="lab-select" value={f.room} onChange={(e) => set({ room: e.target.value })}>
            <option value="">—</option>
            {roomsFor.map((r) => <option key={r.code} value={r.code}>{r.code}{r.name ? ` · ${r.name}` : ""}</option>)}
          </select>
        </Field>
        <Field label="Side">
          <select className="lab-select" value={f.laterality} onChange={(e) => set({ laterality: e.target.value })}>
            {LATERALITY.map((l) => <option key={l || "none"} value={l}>{l || "—"}</option>)}
          </select>
        </Field>
      </div>

      <div className="lab-section">
        <h4>Safety checklist</h4>
        <div className="lab-row" style={{ gap: 20, flexWrap: "wrap" }}>
          {ionising && <label className="lab-check"><input type="checkbox" checked={!!checks.pregnancy} onChange={(e) => setChecks({ ...checks, pregnancy: e.target.checked })} /> Pregnancy screening done</label>}
          {isMR && <label className="lab-check"><input type="checkbox" checked={!!checks.implants} onChange={(e) => setChecks({ ...checks, implants: e.target.checked })} /> No MR-unsafe implants</label>}
          <label className="lab-check"><input type="checkbox" checked={!!checks.allergy} onChange={(e) => setChecks({ ...checks, allergy: e.target.checked })} /> No known contrast allergy</label>
        </div>
        <Field label="Safety notes" hint="Printed in the report's technique block, e.g. LMP 12-Sep">
          <input className="lab-input w-full" value={f.safetyNotes} onChange={(e) => set({ safetyNotes: e.target.value })} />
        </Field>
      </div>

      <div className="lab-section">
        <h4>
          Contrast
          <label className="lab-check" style={{ fontWeight: 400 }}>
            <input type="checkbox" checked={contrast.used} onChange={(e) => setContrast({ ...contrast, used: e.target.checked })} /> Contrast given
          </label>
        </h4>
        {contrast.used && (
          <div className="lab-grid cols-3">
            <Field label="Agent"><input className="lab-input" placeholder="Iohexol" value={contrast.agent} onChange={(e) => setContrast({ ...contrast, agent: e.target.value })} /></Field>
            <Field label="Dose"><input className="lab-input" placeholder="80 ml" value={contrast.dose} onChange={(e) => setContrast({ ...contrast, dose: e.target.value })} /></Field>
            <Field label="Route">
              <select className="lab-select" value={contrast.route} onChange={(e) => setContrast({ ...contrast, route: e.target.value })}>
                {CONTRAST_ROUTES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </Field>
          </div>
        )}
      </div>

      <Field label="Note for the radiologist" hint="Optional, e.g. patient uncooperative">
        <input className="lab-input w-full" value={f.note} onChange={(e) => set({ note: e.target.value })} />
      </Field>
    </Modal>
  );
};

export default AcquireModal;
